import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import {
  Search,
  Star,
  ExternalLink,
  Sparkles,
  MessageCircle,
  Image as ImageIcon,
  Film,
  Music,
  Code2,
  Workflow,
  Briefcase,
  Presentation,
  TrendingUp,
  Video,
  BookOpen,
  Building2,
  Grid3x3,
  CheckCircle2,
  AlertCircle,
  Flag,
  X,
  Bookmark,
  Heart,
  Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  MessageCircle, Image: ImageIcon, Film, Music, Code2, Workflow, Briefcase,
  Presentation, TrendingUp, Video, BookOpen, Building2, Sparkles,
};

/* ====================================================================
 * Super-Gruppen für die Tool-Kategorien (2-Ebenen-Navigation).
 * Key = Group-ID, sub_keys = exact keys returned by /api/tools/categories
 * Tools in keiner Sub-Liste landen automatisch unter "sonstiges".
 * ================================================================= */
const SUPER_GROUPS = [
  { key: "chat", label: "Chat & Sprache", icon: MessageCircle, hex: "#00e5ff", sub_keys: ["chatbots", "Übersetzung & Sprache", "Kundenservice & Support"] },
  { key: "kreativ", label: "Kreativ", icon: Sparkles, hex: "#a855f7", sub_keys: ["bild-design", "video", "audio-musik", "avatar", "3D, Design & Architektur", "Social Media & Content"] },
  { key: "coding", label: "Coding & Tech", icon: Code2, hex: "#2ecc71", sub_keys: ["coding", "No-Code & App-Builder", "KI-Infrastruktur & APIs", "Cybersecurity & IT-KI"] },
  { key: "automation", label: "Automation & Agenten", icon: Workflow, hex: "#ec4899", sub_keys: ["automation", "KI-Browser & Agenten"] },
  { key: "business", label: "Business", icon: Briefcase, hex: "#f39c12", sub_keys: ["produktivitaet", "praesentation", "seo-marketing", "business", "E-Commerce & Retail-KI", "Finanzen & FP&A", "HR & Recruiting", "Legal & Compliance", "Daten & Analytics"] },
  { key: "wissen", label: "Wissen & Bildung", icon: BookOpen, hex: "#14b8a6", sub_keys: ["Bildung & Lernen", "recherche"] },
  { key: "sonstiges", label: "Sonstiges", icon: Grid3x3, hex: "#94a3b8", sub_keys: ["spezial", "Gesundheit & Wellness"] },
];

function resolveSuperGroup(catKey) {
  for (const g of SUPER_GROUPS) if (g.sub_keys.includes(catKey)) return g.key;
  return "sonstiges";
}

/* ===================================================================== */
function ToolLogo({ tool, size = 48 }) {
  const [src, setSrc] = useState(tool.logo_url || tool.favicon_url || "");
  const [failed, setFailed] = useState(false);
  useEffect(() => { setSrc(tool.logo_url || tool.favicon_url || ""); setFailed(false); }, [tool.id, tool.logo_url, tool.favicon_url]);

  const handleError = () => {
    // Fallback chain: logo.clearbit → google favicon → initials
    if (src === tool.logo_url && tool.favicon_url) {
      setSrc(tool.favicon_url);
    } else {
      setFailed(true);
    }
  };

  if (failed || !src) {
    return (
      <div
        className="flex items-center justify-center bg-[#00e5ff]/10 border border-[#00e5ff]/30 text-[#00e5ff] font-outfit font-bold"
        style={{ width: size, height: size, fontSize: size * 0.35 }}
      >
        {tool.name.slice(0, 2).toUpperCase()}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center bg-white p-1.5" style={{ width: size, height: size }}>
      <img src={src} alt={tool.name} onError={handleError} className="max-w-full max-h-full object-contain" loading="lazy" />
    </div>
  );
}

/* ===================================================================== */
function StarRating({ value, onChange, size = 14, readOnly = false, testId }) {
  const [hover, setHover] = useState(0);
  if (readOnly) {
    return (
      <div className="flex items-center gap-0.5" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = (value || 0) >= n;
          return <Star key={n} size={size} className={filled ? "fill-[#00e5ff] text-[#00e5ff]" : "text-white/25"} strokeWidth={1.5} />;
        })}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value || 0) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange?.(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="cursor-pointer transition-transform hover:scale-110"
            aria-label={`${n} Sterne`}
          >
            <Star size={size} className={filled ? "fill-[#00e5ff] text-[#00e5ff]" : "text-white/25"} strokeWidth={1.5} />
          </button>
        );
      })}
    </div>
  );
}

/* ===================================================================== */
function ToolCard({ tool, onClick, onFavorite, onRecommend }) {
  const myStatus = tool.my_status;
  return (
    <div
      data-testid={`tool-card-${tool.id}`}
      className="group relative text-left bg-gradient-to-br from-white/[0.02] to-transparent border border-white/[0.07] hover:border-[#00e5ff]/40 transition-all duration-300 p-5 hover:-translate-y-0.5 hover:shadow-[0_0_24px_-6px_rgba(0,229,255,0.25)]"
    >
      {/* Toolbox status badge */}
      {myStatus && (
        <div className={`absolute top-2 right-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-outfit font-bold border ${myStatus === "active" ? "text-[#2ecc71] border-[#2ecc71]/50 bg-[#2ecc71]/10" : "text-amber-400 border-amber-400/50 bg-amber-400/10"}`} data-testid={`tool-status-${tool.id}`}>
          {myStatus === "active" ? "Aktiv" : "Wishlist"}
        </div>
      )}

      <button onClick={() => onClick(tool)} className="w-full text-left">
        <div className="flex items-start gap-4 mb-3">
          <ToolLogo tool={tool} size={52} />
          <div className="min-w-0 flex-1 pr-16">
            <h3 className="font-outfit font-bold text-white text-[16px] tracking-tight truncate">{tool.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <StarRating value={Math.round(tool.average_rating || 0)} readOnly size={11} />
              {tool.rating_count > 0 ? (
                <span className="text-[11px] text-white/45 font-outfit">
                  {tool.average_rating.toFixed(1)} · {tool.rating_count}
                </span>
              ) : (
                <span className="text-[11px] text-white/25 font-outfit">noch keine Bewertung</span>
              )}
            </div>
          </div>
        </div>
        <p className="text-[13px] text-white/65 leading-relaxed line-clamp-3">{tool.description}</p>
      </button>

      <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFavorite(tool, myStatus === "active" ? "none" : "active"); }}
            data-testid={`tool-fav-active-${tool.id}`}
            title={myStatus === "active" ? "Von aktiv entfernen" : "Als aktiv markieren"}
            className={`h-8 px-2 text-[10px] uppercase tracking-wider font-outfit font-semibold border transition-colors ${myStatus === "active" ? "bg-[#2ecc71]/15 border-[#2ecc71]/60 text-[#2ecc71]" : "border-white/10 text-white/50 hover:text-[#2ecc71] hover:border-[#2ecc71]/50"}`}
          >
            <Heart size={11} className="inline -mt-0.5 mr-1" /> Aktiv
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFavorite(tool, myStatus === "wishlist" ? "none" : "wishlist"); }}
            data-testid={`tool-fav-wish-${tool.id}`}
            title={myStatus === "wishlist" ? "Von Wishlist entfernen" : "Auf Wishlist setzen"}
            className={`h-8 px-2 text-[10px] uppercase tracking-wider font-outfit font-semibold border transition-colors ${myStatus === "wishlist" ? "bg-amber-400/15 border-amber-400/60 text-amber-400" : "border-white/10 text-white/50 hover:text-amber-400 hover:border-amber-400/50"}`}
          >
            <Bookmark size={11} className="inline -mt-0.5 mr-1" /> Probieren
          </button>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRecommend(tool); }}
          data-testid={`tool-recommend-${tool.id}`}
          title="Am schwarzen Brett empfehlen"
          className="h-8 px-2.5 text-[10px] uppercase tracking-wider font-outfit font-semibold border border-[#00e5ff]/30 text-[#00e5ff]/80 hover:bg-[#00e5ff]/10 hover:text-[#00e5ff] transition-colors"
        >
          <Send size={11} className="inline -mt-0.5 mr-1" /> Empfehlen
        </button>
      </div>
    </div>
  );
}

/* ===================================================================== */
function ReportDialog({ open, onOpenChange, target_type, target_id, target_name }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (reason.trim().length < 3) { setErr("Bitte kurz beschreiben, was nicht stimmt."); return; }
    setSubmitting(true); setErr("");
    try {
      await axios.post(`${API}/reports/`, { target_type, target_id, target_name, reason }, { withCredentials: true });
      setSent(true);
      setTimeout(() => { onOpenChange(false); setSent(false); setReason(""); }, 1500);
    } catch (e) { setErr(e?.response?.data?.detail || "Senden fehlgeschlagen"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-red-400/30 text-white rounded-none max-w-md p-0" data-testid="report-dialog">
        <div className="p-6">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Flag size={14} className="text-red-400" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-400/80 font-outfit font-semibold">Meldung an Admin</span>
            </div>
            <DialogTitle className="font-outfit font-bold text-xl text-white tracking-tight">Fehler / falsche Info melden</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-white/55 mt-2 mb-4">Bezieht sich auf: <b className="text-white/85">{target_name || target_type}</b></p>
          <Textarea
            value={reason} onChange={(e) => setReason(e.target.value.slice(0, 600))}
            placeholder="Was stimmt nicht? z.B. Link ist tot, falsche Kategorie, Tool existiert nicht mehr …"
            data-testid="report-reason" autoFocus
            className="bg-black border-white/10 text-white rounded-none focus:border-red-400/60 focus:ring-0 min-h-[100px] text-sm"
          />
          {err && <p className="text-[13px] text-red-400 mt-2">{err}</p>}
          {sent && <p className="text-[13px] text-[#00e5ff] mt-2">Danke! Deine Meldung ist beim Admin angekommen.</p>}
          <div className="flex gap-2 mt-4">
            <Button onClick={submit} disabled={submitting || sent || reason.trim().length < 3} data-testid="report-submit"
              className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-none font-outfit font-semibold h-10 text-sm">
              {submitting ? "…" : sent ? "Gesendet" : "Meldung senden"}
            </Button>
            <Button type="button" onClick={() => onOpenChange(false)} variant="ghost"
              className="border border-white/15 text-white/70 hover:text-white rounded-none h-10 px-4 text-sm">
              <X size={14} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===================================================================== */
function ToolDetailDialog({ toolId, open, onOpenChange, onRatingChanged }) {
  const { user } = useAuth();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myReview, setMyReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    if (!open || !toolId) return;
    setLoading(true); setError("");
    Promise.all([
      axios.get(`${API}/tools/${toolId}`, { withCredentials: true }),
      axios.get(`${API}/tools/${toolId}/ratings`, { withCredentials: true }),
    ])
      .then(([t, r]) => {
        setTool(t.data);
        setRatings(r.data);
        setMyRating(t.data.user_rating || 0);
        const mine = r.data.find((x) => x.user_id === (user?._id || user?.id));
        setMyReview(mine?.review_text || "");
      })
      .catch((e) => setError(e?.response?.data?.detail || "Tool konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [open, toolId, user]);

  const submitRating = async () => {
    if (!myRating || !tool) return;
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/tools/${tool.id}/rate`, { rating: myRating, review_text: myReview || null }, { withCredentials: true });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      // Refresh ratings
      const [t, r] = await Promise.all([
        axios.get(`${API}/tools/${tool.id}`, { withCredentials: true }),
        axios.get(`${API}/tools/${tool.id}/ratings`, { withCredentials: true }),
      ]);
      setTool(t.data); setRatings(r.data);
      onRatingChanged?.(t.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Bewertung konnte nicht gespeichert werden.");
    } finally { setSubmitting(false); }
  };

  if (!tool && !loading) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-3xl p-0 max-h-[92vh] overflow-y-auto"
        data-testid="tool-detail-dialog"
      >
        {loading && <div className="p-10 text-center text-white/50">Lädt…</div>}
        {!loading && tool && (
          <>
            <DialogHeader className="p-6 border-b border-white/10 bg-black/40">
              <div className="flex items-start gap-4">
                <ToolLogo tool={tool} size={64} />
                <div className="min-w-0 flex-1">
                  <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">
                    {tool.name}
                  </DialogTitle>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.26em] text-[#00e5ff]/80 font-outfit font-semibold border border-[#00e5ff]/30 px-2 py-1">
                      {tool.category.replace(/-/g, " ")}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <StarRating value={Math.round(tool.average_rating || 0)} readOnly size={12} />
                      <span className="text-xs text-white/60 font-outfit">
                        {tool.rating_count > 0 ? `${tool.average_rating.toFixed(1)} (${tool.rating_count})` : "noch keine Bewertung"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="p-6 space-y-6">
              {/* Short description */}
              <p className="text-white/80 text-[15px] leading-relaxed">{tool.description}</p>

              {/* Long description (LLM-generated) */}
              {tool.long_description && (
                <div className="prose prose-invert max-w-none prose-headings:font-outfit prose-headings:text-white prose-p:text-white/75 prose-strong:text-[#00e5ff] prose-li:text-white/75 prose-li:marker:text-[#00e5ff]/60 text-sm leading-relaxed">
                  <ReactMarkdown>{tool.long_description}</ReactMarkdown>
                </div>
              )}

              {/* Visit + Report buttons */}
              <div className="flex items-center gap-3 pt-2 flex-wrap">
                <Button
                  onClick={() => window.open(tool.website_url, "_blank", "noopener,noreferrer")}
                  data-testid="tool-visit-btn"
                  className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 px-5 text-sm tracking-wide"
                >
                  Zur Website
                  <ExternalLink size={14} className="ml-2" />
                </Button>
                <a
                  href={tool.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-white/40 hover:text-[#00e5ff]/80 transition-colors truncate flex-1 min-w-0"
                >
                  {tool.website_url}
                </a>
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  data-testid="tool-report-btn"
                  className="text-[11px] uppercase tracking-[0.18em] font-outfit font-semibold text-red-400/70 hover:text-red-400 border border-red-400/20 hover:border-red-400/40 px-3 h-9 flex items-center gap-1.5 transition-colors"
                >
                  <Flag size={12} /> Fehler melden
                </button>
              </div>

              {/* Screenshots placeholder */}
              {Array.isArray(tool.screenshots) && tool.screenshots.length > 0 && (
                <div>
                  <h4 className="font-outfit text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]/80 mb-3 font-semibold">
                    Screenshots
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {tool.screenshots.map((url, i) => (
                      <img key={`${url}-${i}`} src={url} alt={`Screenshot ${i + 1}`} className="border border-white/10" />
                    ))}
                  </div>
                </div>
              )}

              {/* Rating section */}
              <div className="border-t border-white/10 pt-5">
                <h4 className="font-outfit text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]/80 mb-4 font-semibold">
                  Deine Bewertung
                </h4>
                {error && (
                  <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2 mb-3 flex items-center gap-2" data-testid="rating-error">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}
                <div className="flex items-center gap-4 mb-3">
                  <StarRating value={myRating} onChange={setMyRating} size={22} testId="my-rating" />
                  {myRating > 0 && <span className="text-sm text-white/60 font-outfit">{myRating} / 5</span>}
                </div>
                <Textarea
                  value={myReview}
                  onChange={(e) => setMyReview(e.target.value.slice(0, 500))}
                  placeholder="Kurze Notiz zu deiner Erfahrung (optional, max. 500 Zeichen)…"
                  data-testid="my-review-textarea"
                  className="bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[70px] text-sm mb-3"
                />
                <Button
                  onClick={submitRating}
                  disabled={!myRating || submitting}
                  data-testid="submit-rating-btn"
                  className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none font-outfit font-semibold h-10 px-5 text-sm"
                >
                  {submitting ? "…" : saved ? <><CheckCircle2 size={14} className="mr-2" /> Gespeichert</> : "Bewertung speichern"}
                </Button>
              </div>

              {/* Other reviews */}
              {ratings.length > 0 && (
                <div className="border-t border-white/10 pt-5">
                  <h4 className="font-outfit text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]/80 mb-3 font-semibold">
                    Alle Bewertungen ({ratings.length})
                  </h4>
                  <div className="space-y-3">
                    {ratings.map((r) => (
                      <div key={r.id || `${r.user_id}-${r.created_at}`} className="border border-white/[0.06] p-3 bg-white/[0.015]">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[13px] font-outfit font-semibold text-white">{r.user_name}</span>
                          <StarRating value={r.rating} readOnly size={11} />
                        </div>
                        {r.review_text && <p className="text-[13px] text-white/70 leading-relaxed">{r.review_text}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        target_type="tool"
        target_id={tool?.id || ""}
        target_name={tool?.name || ""}
      />
    </Dialog>
  );
}

/* ===================================================================== */
export default function ToolsPage() {
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("rating");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toolboxOnly, setToolboxOnly] = useState(false);
  const [superGroup, setSuperGroup] = useState("all");  // "all" | <group_key>

  // Sub-category counts per super-group
  const groupCounts = useMemo(() => {
    const counts = Object.fromEntries(SUPER_GROUPS.map((g) => [g.key, 0]));
    for (const c of categories) {
      const g = resolveSuperGroup(c.key);
      counts[g] = (counts[g] || 0) + (c.count || 0);
    }
    return counts;
  }, [categories]);

  // Categories visible in sub-row (based on selected super-group)
  const visibleSubCategories = useMemo(() => {
    if (superGroup === "all") return [];
    const g = SUPER_GROUPS.find((x) => x.key === superGroup);
    if (!g) return [];
    return categories.filter((c) => g.sub_keys.includes(c.key));
  }, [superGroup, categories]);

  // When switching super-group, reset the detail category selection
  useEffect(() => {
    if (superGroup === "all") { setCategory("all"); return; }
    const g = SUPER_GROUPS.find((x) => x.key === superGroup);
    if (!g) return;
    if (category !== "all" && !g.sub_keys.includes(category)) setCategory("all");
  }, [superGroup]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tools/categories`, { withCredentials: true });
      setCategories(data);
    } catch (err) {
      console.debug("[tools] loadCategories failed:", err?.response?.status);
    }
  }, []);

  const loadTools = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tools/`, {
        params: { category: category === "all" ? undefined : category, q: q || undefined, sort },
        withCredentials: true,
      });
      setTools(data);
    } catch (err) {
      console.error("[tools] loadTools failed:", err?.response?.status, err?.message);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [category, q, sort]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    const t = setTimeout(loadTools, 200); // debounce search
    return () => clearTimeout(t);
  }, [loadTools]);

  const displayTools = useMemo(() => {
    let t = tools;
    // Super-group client-side filter when no sub-category picked
    if (superGroup !== "all" && category === "all") {
      const g = SUPER_GROUPS.find((x) => x.key === superGroup);
      if (g) t = t.filter((x) => g.sub_keys.includes(x.category));
    }
    if (toolboxOnly) t = t.filter((x) => x.my_status);
    return t;
  }, [tools, toolboxOnly, superGroup, category]);
  const totalTools = useMemo(() => categories.reduce((s, c) => s + (c.count || 0), 0), [categories]);

  const onCardClick = (tool) => { setSelectedId(tool.id); setDialogOpen(true); };

  const onFavorite = async (tool, status) => {
    try {
      const { data } = await axios.post(`${API}/tools/${tool.id}/favorite`, { status }, { withCredentials: true });
      setTools((prev) => prev.map((t) => (t.id === tool.id ? { ...t, my_status: data.status === "none" ? null : data.status } : t)));
    } catch (err) {
      alert(err?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const [recommendTool, setRecommendTool] = useState(null);
  const onRecommend = (tool) => setRecommendTool(tool);
  const sendRecommendation = async (title, description) => {
    await axios.post(`${API}/posts`, {
      title: title.slice(0, 80),
      description: description.slice(0, 500),
      category: "Allgemein",
      tool_ref: {
        id: recommendTool.id,
        name: recommendTool.name,
        website_url: recommendTool.website_url,
        logo_url: recommendTool.logo_url || recommendTool.favicon_url || "",
      },
    }, { withCredentials: true });
    setRecommendTool(null);
  };

  const onRatingChanged = (updated) => {
    setTools((prev) => prev.map((t) => (t.id === updated.id ? { ...t, average_rating: updated.average_rating, rating_count: updated.rating_count } : t)));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-px bg-[#00e5ff]" />
            <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
              AI Tool Directory
            </span>
          </div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="tools-heading">
            KI-Tools <span className="text-[#00e5ff]">kuratiert</span>
          </h1>
          <p className="text-base sm:text-lg text-white/55 max-w-2xl leading-relaxed">
            {totalTools} handverlesene KI-Tools aus {categories.length} Kategorien. Bewerte sie mit 1–5 Sternen und entdecke, womit andere Mitglieder arbeiten.
          </p>
          <div className="mt-4 inline-flex items-start gap-2 px-3 py-2 border border-[#00e5ff]/25 bg-[#00e5ff]/[0.04] text-[12px]" data-testid="tools-no-commission-disclaimer">
            <span className="shrink-0 mt-0.5 text-[#00e5ff]/90 font-outfit font-bold text-[10px] uppercase tracking-[0.18em]">Unabhängig</span>
            <span className="text-white/70 leading-relaxed max-w-3xl">
              Keine Provisionen, keine Affiliate-Links. Die Tools werden vollautomatisiert gecrawlt — das <strong className="text-white">Community-Rating</strong> soll dir helfen, dich im Urwald der Möglichkeiten zurechtzufinden.
            </span>
          </div>
        </div>

        {/* Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nach Name oder Funktion suchen…"
              data-testid="tool-search"
              className="pl-10 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSort("rating")}
              data-testid="sort-rating"
              className={`px-4 h-11 text-[13px] font-outfit tracking-wide transition-all ${sort === "rating" ? "bg-[#00e5ff]/[0.1] text-[#00e5ff] border border-[#00e5ff]/40" : "text-white/55 border border-white/10 hover:text-white"}`}
            >
              Top-Bewertung
            </button>
            <button
              onClick={() => setSort("name")}
              data-testid="sort-name"
              className={`px-4 h-11 text-[13px] font-outfit tracking-wide transition-all ${sort === "name" ? "bg-[#00e5ff]/[0.1] text-[#00e5ff] border border-[#00e5ff]/40" : "text-white/55 border border-white/10 hover:text-white"}`}
            >
              A–Z
            </button>
            <button
              onClick={() => setToolboxOnly((v) => !v)}
              data-testid="toolbox-toggle"
              title="Nur meine favorisierten Tools anzeigen"
              className={`px-4 h-11 text-[13px] font-outfit tracking-wide transition-all inline-flex items-center gap-2 ${toolboxOnly ? "bg-[#a855f7]/[0.15] text-[#a855f7] border border-[#a855f7]/50" : "text-white/55 border border-white/10 hover:text-white"}`}
            >
              <Bookmark size={13} /> Meine Toolbox
            </button>
          </div>
        </div>

        {/* ===== Super-group tabs (top level) ===== */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="supergroup-filters">
          <button
            onClick={() => setSuperGroup("all")}
            data-testid="supergroup-all"
            className={`flex items-center gap-2 px-4 h-11 text-[13px] font-outfit tracking-wide transition-all ${superGroup === "all" ? "bg-[#00e5ff] text-black font-bold" : "border border-white/10 text-white/60 hover:border-[#00e5ff]/40 hover:text-white"}`}
          >
            <Grid3x3 size={14} />
            Alle
            <span className="text-[11px] opacity-60">({totalTools})</span>
          </button>
          {SUPER_GROUPS.map((g) => {
            const active = superGroup === g.key;
            const Icon = g.icon;
            return (
              <button
                key={g.key}
                onClick={() => setSuperGroup(g.key)}
                data-testid={`supergroup-${g.key}`}
                style={active ? { backgroundColor: g.hex, color: "#000" } : {}}
                className={`flex items-center gap-2 px-4 h-11 text-[13px] font-outfit tracking-wide transition-all ${active ? "font-bold" : "border border-white/10 text-white/60 hover:border-white/30 hover:text-white"}`}
              >
                <Icon size={14} />
                {g.label}
                <span className="text-[11px] opacity-60">({groupCounts[g.key] || 0})</span>
              </button>
            );
          })}
        </div>

        {/* ===== Sub-category filters (only when a super-group is picked) ===== */}
        {superGroup !== "all" && visibleSubCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8 pl-3 border-l-2" style={{ borderColor: (SUPER_GROUPS.find((g) => g.key === superGroup)?.hex || "#00e5ff") + "60" }} data-testid="subcategory-filters">
            <button
              onClick={() => setCategory("all")}
              data-testid="subcat-all"
              className={`px-3 h-9 text-[12px] font-outfit transition-colors ${category === "all" ? "bg-white/10 text-white border border-white/20" : "text-white/50 border border-white/[0.05] hover:text-white"}`}
            >
              Alle Unterkategorien
            </button>
            {visibleSubCategories.map((c) => {
              const Icon = ICON_MAP[c.icon] || Sparkles;
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`cat-${c.key}`}
                  className={`flex items-center gap-1.5 px-3 h-9 text-[12px] font-outfit transition-colors ${active ? "bg-white/10 text-white border border-white/30" : "text-white/50 border border-white/[0.05] hover:text-white hover:border-white/20"}`}
                >
                  <Icon size={12} />
                  {c.label}
                  <span className="text-[10px] opacity-60">({c.count})</span>
                </button>
              );
            })}
          </div>
        )}

        {superGroup === "all" && <div className="mb-8" />}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`skel-tool-${i}`} className="h-40 border border-white/[0.05] bg-white/[0.015] animate-pulse" />
            ))}
          </div>
        ) : displayTools.length === 0 ? (
          <div className="text-center py-16 text-white/40" data-testid="tools-empty">
            <Sparkles size={32} className="mx-auto mb-3 text-white/30" />
            <p className="font-outfit">{toolboxOnly ? 'Deine Toolbox ist leer — markiere Tools als „Aktiv" oder „Probieren".' : "Keine Tools gefunden."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
            {displayTools.map((t) => <ToolCard key={t.id} tool={t} onClick={onCardClick} onFavorite={onFavorite} onRecommend={onRecommend} />)}
          </div>
        )}
      </main>

      <ToolDetailDialog
        toolId={selectedId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onRatingChanged={onRatingChanged}
      />

      <RecommendDialog tool={recommendTool} open={!!recommendTool} onOpenChange={(v) => !v && setRecommendTool(null)} onSend={sendRecommendation} />
    </div>
  );
}

/* ===================================================================== */
function RecommendDialog({ tool, open, onOpenChange, onSend }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (tool && open) {
      setTitle(`Tool-Tipp: ${tool.name}`);
      setDescription(`Ich nutze ${tool.name} gerade und finde es empfehlenswert. ${tool.description || ""}`.slice(0, 500));
      setSent(false); setErr("");
    }
  }, [tool, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      await onSend(title, description);
      setSent(true);
      setTimeout(() => onOpenChange(false), 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-lg p-6 sm:p-8" data-testid="tool-recommend-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Send size={13} className="text-[#00e5ff]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/80 font-outfit font-semibold">Am schwarzen Brett empfehlen</span>
          </div>
          <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">{tool?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">Titel (max. 80)</label>
            <Input required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="recommend-title" className="mt-2 bg-black border-white/10 text-white rounded-none h-11 text-[14px] focus:border-[#00e5ff]/60 focus:ring-0" />
          </div>
          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">Beschreibung (max. 500)</label>
            <Textarea required maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="recommend-description" className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[110px] text-[14px]" />
          </div>
          {err && <p className="text-[13px] text-red-400" data-testid="recommend-error">{err}</p>}
          {sent && <p className="text-[13px] text-[#2ecc71]">Empfehlung am Brett veröffentlicht ✓</p>}
          <Button type="submit" disabled={submitting || sent} data-testid="recommend-submit-btn" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
            {submitting ? "..." : sent ? "Veröffentlicht ✓" : "Am Brett veröffentlichen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
